import React, { useState, useImperativeHandle, forwardRef, useEffect, useRef } from "react";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import { IFilterComp, IFilterParams } from "ag-grid-community";
import { Chip, IconButton, InputAdornment, Box, makeStyles, ClickAwayListener, Typography } from "@material-ui/core";
import ClearIcon from "@material-ui/icons/Clear";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { DateField, Paper } from "@roc/ui";
import { format, isValid } from "date-fns";
import ReactDOM from "react-dom";

const useStyles = makeStyles(theme => ({
  root: {
    "& .MuiOutlinedInput-root": {
      padding: "0 6px",
      height: 26,
      minHeight: 26,
      display: "flex",
      alignItems: "center",
      backgroundColor: "#fff",
    },
    "& .MuiInputBase-input": {
      padding: "2px 0",
      fontSize: 12,
    },
  },
}));

export const MultipleSearchFloatingFilter = forwardRef((props: any, ref) => {
  const classes = useStyles();
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [input, setInput] = useState<string>("");
  const inputRef = React.useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    onParentModelChanged(parentModel: any) {
      const vals: string[] = parentModel?.selectedOptions ?? [];
      setSelectedValues(Array.isArray(vals) ? vals : []);
      setInput(Array.isArray(vals) ? vals.join(',') : "");
    },
    getModelAsString() {
      return selectedValues.join(", ");
    },
  }));


  const addInputAsValues = (raw: string) => {
    if (!raw) return;
    const parts = raw
      .split(",")
      .map((p) => p.trim())
      .filter((p) => p.length > 0);

    if (parts.length === 0) {
      setInput(selectedValues.length > 0 ? selectedValues.join(',') : "");
      return;
    }

    const lowerSet = new Set(selectedValues.map((s) => s.toLowerCase()));
    const newOnes = parts.filter((p) => !lowerSet.has(p.toLowerCase()));

    if (newOnes.length === 0) {
      setInput(selectedValues.length > 0 ? selectedValues.join(',') : "");
      return;
    }

    const updated = [...selectedValues, ...newOnes];
    setSelectedValues(updated);

    props.parentFilterInstance((instance: IFilterComp) => {
      instance.setModel({ selectedOptions: updated });
    });
    if (props.filterChangedCallback) props.filterChangedCallback();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setTimeout(() => {
        inputRef.current?.blur();
      }, 0);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInput(value);
  };

  const handleBlur = () => {
    if (input.trim()) {
      addInputAsValues(input);
    } else {
      setInput(selectedValues.length > 0 ? selectedValues.join(',') : "");
    }

  };

  const handleFocus = () => {
    setInput("");
  };

  const handleClear = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedValues([]);

    props.parentFilterInstance((instance: IFilterComp) => {
      instance.setModel(null);
    });
    if (props.filterChangedCallback) props.filterChangedCallback();
  };

  useEffect(() => {
    const pm = (props as any).parentModel;
    if (pm && Array.isArray(pm.selectedOptions)) {
      setSelectedValues(pm.selectedOptions);
    }
  }, [props.parentModel]);

  return (
    <TextField
      fullWidth
      inputRef={inputRef}
      value={input}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      placeholder=""
      size="small"
      variant="outlined"
      className={classes.root}
      InputProps={{
        endAdornment: (selectedValues.length > 0 ? (<InputAdornment position="end">
          <IconButton size="small" onClick={handleClear} aria-label="clear">
            <ClearIcon fontSize="small" />
          </IconButton>
        </InputAdornment>) : (<></>)
        ),
      }}
      onFocus={handleFocus}
      onMouseDown={(e) => e.stopPropagation()}
    />
  );
});

export const MultipleSearchFilter = forwardRef((props: IFilterParams, ref) => {
  const selectedValues = useRef<string[]>([]);

  useImperativeHandle(ref, () => {
    return {
      doesFilterPass(params) {
        return true;
      },

      isFilterActive() {
        return selectedValues.current?.length > 0;
      },

      getModel() {
        if (selectedValues.current?.length > 0) {
          return {
            selectedOptions: selectedValues.current,
            filterType: 'select',
            type: 'contains',
          };
        }
        return null;
      },

      setModel(model) {
        selectedValues.current = model?.selectedOptions ?? [];
        props.filterChangedCallback();
      },

      onFloatingFilterChanged(type, value) {
        selectedValues.current = value;
        props.filterChangedCallback();
      },
    };
  });

  return null;
});